// Platform-settings registry — single source of truth for the admin
// /admin/settings page. Each entry describes one tunable that was previously
// a hard-coded constant in code.
//
// Pure module — no DB, no runtime deps. The worker's
// services/platform-settings.ts imports this to know the keys, defaults,
// and bounds; the admin SPA imports the same const to render labels,
// descriptions, and tab groupings.
//
// Adding a setting: append a new entry. Update the call sites (sweeper /
// service) to read `await getSetting(env, '<key>', registryDefault)`.
//
// Key naming convention:
//   - `<thing>_days` for day-window settings (sweepers convert to ms)
//   - `<thing>_hours` for hour-window settings (state machines convert)
//   - `<thing>_aud` for displayed price values
//   - bare names for counts (cohort cap)
//
// Units are stored verbatim — no auto-conversion. Sweepers + state machines
// multiply at the read boundary. This keeps the admin UI showing the same
// number Richie typed, no surprises.

/** Tab grouping in the admin UI. */
export type PlatformSettingCategory = 'numbers' | 'sla' | 'pricing' | 'privacy' | 'branding';

export const PLATFORM_SETTING_CATEGORY_LABELS: Record<PlatformSettingCategory, string> = {
  numbers: 'Numbers',
  sla: 'SLA',
  pricing: 'Pricing',
  privacy: 'Privacy',
  branding: 'Branding',
};

/** Discriminator stored alongside each value so the parser knows what to JSON-parse to. */
export type PlatformSettingValueType = 'number' | 'string' | 'boolean' | 'json';

/**
 * Setting definition. `default` is the value used when a row is missing OR
 * when reads fall back due to a KV+D1 blip. All current settings are numeric;
 * the `string` / `boolean` / `json` shapes are reserved for future use.
 */
export type PlatformSettingDef = {
  key: string;
  label: string;
  description: string;
  category: PlatformSettingCategory;
  valueType: PlatformSettingValueType;
  /** Default value — used both as the cold-read fallback and as the registry "from" baseline for audits. */
  default: number | string | boolean;
  /** Optional bounds for number settings; admin UI uses these as input constraints. */
  min?: number;
  max?: number;
  /** Optional unit suffix shown in the admin UI ("days" / "hours" / "AUD"). */
  unit?: string;
  /** Optional allowed values for string settings; admin UI renders as a select/radio. */
  allowedValues?: readonly string[];
  /** Optional regex pattern for string validation (e.g. hex color). */
  pattern?: string;
  /** Plain-English note about what changes when this value moves. Surfaces in the admin UI as help text. */
  policyNote: string;
};

const DAY = 24;
const HOUR_OF_DAY = 24;
// Aliases so the registry reads more like English; SAT both refer to the same 24h.
void DAY;
void HOUR_OF_DAY;

/**
 * Full settings registry. Keys are stable — never rename without a migration,
 * because they're persisted in `platform_settings.key`.
 */
export const PLATFORM_SETTINGS = [
  // ─── Numbers ─────────────────────────────────────────────────────────────
  {
    key: 'founding_cohort_cap',
    label: 'Founding cohort cap',
    description: 'Total number of venues that can claim a founding-cohort position.',
    category: 'numbers',
    valueType: 'number',
    default: 50,
    min: 1,
    max: 1000,
    policyNote:
      'Raising the cap reopens enrolment; lowering below the current claimed count freezes new claims but does not revoke existing badges.',
  },
  {
    key: 'review_window_days',
    label: 'Review window',
    description: 'Days after exchange completion before unsubmitted drafts auto-close as closed_unreviewed.',
    category: 'numbers',
    valueType: 'number',
    default: 14,
    min: 1,
    max: 90,
    unit: 'days',
    policyNote:
      'Applied retroactively — the next sweeper tick re-evaluates every open review against the new window.',
  },
  {
    key: 'mutual_release_window_days',
    label: 'Mutual-release window',
    description: 'Days after exchange completion before a lone submitted review auto-releases without its pair.',
    category: 'numbers',
    valueType: 'number',
    default: 14,
    min: 1,
    max: 90,
    unit: 'days',
    policyNote:
      'Should typically equal the review window; setting it shorter releases lone reviews before drafts close.',
  },
  {
    key: 'no_response_remind_first_days',
    label: 'No-response first reminder',
    description: 'Days after a request is sent before the host gets a first nudge.',
    category: 'numbers',
    valueType: 'number',
    default: 30,
    min: 1,
    max: 180,
    unit: 'days',
    policyNote: 'Applies to exchange requests still in the submitted state.',
  },
  {
    key: 'no_response_remind_second_days',
    label: 'No-response second reminder',
    description: 'Days after request before the host gets the second (final) nudge.',
    category: 'numbers',
    valueType: 'number',
    default: 60,
    min: 1,
    max: 365,
    unit: 'days',
    policyNote: 'Should be greater than the first reminder; smaller value disables the second nudge effectively.',
  },
  {
    key: 'no_response_auto_cancel_days',
    label: 'No-response auto-cancel',
    description: 'Days after request before an un-responded exchange auto-cancels.',
    category: 'numbers',
    valueType: 'number',
    default: 90,
    min: 1,
    max: 365,
    unit: 'days',
    policyNote: 'Should be greater than both reminder windows; otherwise reminders never fire before cancel.',
  },
  {
    key: 'first_period_days',
    label: 'Subscription trial length',
    description: 'Days a trial subscription runs before the first paid period activates.',
    category: 'numbers',
    valueType: 'number',
    default: 30,
    min: 1,
    max: 365,
    unit: 'days',
    policyNote: 'Trial-end notifications fire at 30d-out and 7d-out (both derived from this value).',
  },
  {
    key: 'verification_expiry_warn_days',
    label: 'Verification expiry warning',
    description: 'Days before a verification expires that the holder gets a renewal nudge.',
    category: 'numbers',
    valueType: 'number',
    default: 30,
    min: 1,
    max: 180,
    unit: 'days',
    policyNote: 'Lower values mean later warnings (less notice). Renewal nudges fire regardless of whether auto-revoke on expiry is switched on.',
  },
  {
    key: 'verification_auto_revoke_on_expiry',
    label: 'Auto-revoke verifications on expiry',
    description:
      'When on, the daily sweeper revokes any approved verification whose expiry date has passed (and nudges holders ahead of time). When off (the MVP default), expiry dates are recorded but never enforced — verification is a one-time check.',
    category: 'numbers',
    valueType: 'boolean',
    default: false,
    policyNote:
      'Off for MVP: a one-time verification check with no auto-revoke. Turning this on means an expired verification silently loses its Verified status — which now blocks the host-confirm booking gate (both parties must be Verified). Only enable once a self-serve renewal flow exists.',
  },
  {
    key: 'account_close_grace_days',
    label: 'Account-close grace window',
    description: 'Days between an account-close request and the final PII anonymisation.',
    category: 'numbers',
    valueType: 'number',
    default: 30,
    min: 1,
    max: 90,
    unit: 'days',
    policyNote:
      'A user can restore their account any time within this window. Shorter windows reduce reversibility.',
  },
  // ─── SLA ─────────────────────────────────────────────────────────────────
  {
    key: 'dispute_ack_sla_hours',
    label: 'Dispute acknowledgement SLA (breach)',
    description: 'Hours from dispute open before lack of acknowledgement is flagged as breached.',
    category: 'sla',
    valueType: 'number',
    default: 48,
    min: 1,
    max: 240,
    unit: 'hours',
    policyNote: 'Applied retroactively — already-open disputes are re-evaluated on the next sweeper tick.',
  },
  {
    key: 'dispute_ack_at_risk_hours',
    label: 'Dispute acknowledgement SLA (at-risk)',
    description: 'Hours from dispute open before lack of acknowledgement is flagged as at-risk.',
    category: 'sla',
    valueType: 'number',
    default: 36,
    min: 1,
    max: 240,
    unit: 'hours',
    policyNote: 'Should be less than the breach SLA. Values >= breach disable the at-risk warning.',
  },
  // ─── Pricing ─────────────────────────────────────────────────────────────
  {
    key: 'pricing_monthly_aud',
    label: 'Standard monthly price',
    description: 'Displayed host subscription price per month.',
    category: 'pricing',
    valueType: 'number',
    default: 19,
    min: 0,
    max: 10_000,
    unit: 'AUD',
    policyNote: 'Display only until Stripe goes live — does not affect any current billing.',
  },
  {
    key: 'pricing_yearly_aud',
    label: 'Standard yearly price',
    description: 'Displayed host subscription price per year.',
    category: 'pricing',
    valueType: 'number',
    default: 228,
    min: 0,
    max: 100_000,
    unit: 'AUD',
    policyNote: 'Display only until Stripe goes live.',
  },
  {
    key: 'pricing_founding_monthly_aud',
    label: 'Founding cohort monthly price',
    description:
      'Grandfathered monthly price for founding-cohort venues — the rate locked for life after their free first year (Build Brief §2).',
    category: 'pricing',
    valueType: 'number',
    default: 19,
    min: 0,
    max: 10_000,
    unit: 'AUD',
    policyNote:
      'Display only until Stripe goes live. Founding venues pay nothing in year one (see the subscription trial length); this is the locked rate that applies afterwards.',
  },
  // ─── Privacy ─────────────────────────────────────────────────────────────
  {
    key: 'address_visibility_mode',
    label: 'Venue address visibility',
    description:
      'Controls when a venue’s full street address is revealed. confirmed_exchange = only after a host confirms an exchange; always_visible = shown on the public venue page; host_controlled = each host decides via their venue settings.',
    category: 'privacy',
    valueType: 'string',
    default: 'confirmed_exchange',
    allowedValues: ['confirmed_exchange', 'always_visible', 'host_controlled'] as const,
    policyNote:
      'Changing to always_visible immediately exposes every venue address on their public page. host_controlled defers to the per-venue toggle (defaults to hidden until the host opts in).',
  },
  {
    key: 'mobile_verification_mode',
    label: 'Mobile verification mode',
    description:
      'How the mobile-number check behaves while Twilio SMS is not configured. accept_any = no text is sent and any 6-digit code completes verification (preview/testing — works without a mobile phone); sms = require a real code, so sends fail with an honest "not switched on yet" message until Twilio is provisioned.',
    category: 'privacy',
    valueType: 'string',
    default: 'accept_any',
    allowedValues: ['accept_any', 'sms'] as const,
    policyNote:
      'accept_any is only honoured while the TWILIO_* secrets are unbound — once real SMS is provisioned at launch, codes are enforced regardless of this setting, so preview mode cannot be left on by accident. Every verification minted under accept_any is audit-logged (verification.mobile_mock_accept) so mock-era rows can be identified later.',
  },
  // ─── Branding ────────────────────────────────────────────────────────────
  {
    key: 'brand_primary_hex',
    label: 'Primary brand colour',
    description: 'The source colour for the entire brand palette. System generates 10 shades automatically. Brand Pack v3 default is Clay #D2602E.',
    category: 'branding',
    valueType: 'string',
    default: '#d2602e',
    pattern: '^#[0-9a-fA-F]{6}$',
    policyNote:
      'Changing this recolours the entire site on next page load — buttons, links, cards, gradients, focus rings. Audit trail lets you revert instantly.',
  },
  {
    key: 'brand_accent_hex',
    label: 'Accent / gradient colour',
    description: 'Secondary colour used in gradients and accent elements. Brand Pack v3 default is Sun #E9A23B.',
    category: 'branding',
    valueType: 'string',
    default: '#e9a23b',
    pattern: '^#[0-9a-fA-F]{6}$',
    policyNote:
      'Used as the gradient endpoint alongside the primary colour. Visible in headers, section titles, and call-to-action highlights.',
  },
  {
    key: 'brand_warmth_enabled',
    label: 'Paper warmth',
    description: 'Toggle the parchment-tinted background and cream card surfaces. Off = clean neutral white/grey.',
    category: 'branding',
    valueType: 'boolean',
    default: true,
    policyNote:
      'The warmth layer is a key part of the grassroots identity. Turning it off shifts to a modern neutral look.',
  },
  {
    key: 'brand_grain_enabled',
    label: 'Paper grain texture',
    description: 'Toggle the subtle paper-grain noise overlay visible in light mode.',
    category: 'branding',
    valueType: 'boolean',
    default: true,
    policyNote:
      'The grain adds tactile depth. Turning it off gives a cleaner, flatter feel. No effect in dark mode.',
  },
  {
    key: 'brand_font',
    label: 'Brand body font',
    description: 'Font family for body text and UI. Headlines use Bricolage Grotesque and the logo uses Darker Grotesque regardless. Brand Pack v3 default is Hanken Grotesk.',
    category: 'branding',
    valueType: 'string',
    default: 'Hanken Grotesk',
    allowedValues: [
      'Hanken Grotesk',
      'Bricolage Grotesque',
      'Inter',
      'DM Sans',
      'Plus Jakarta Sans',
      'Nunito Sans',
    ] as const,
    policyNote:
      'Font loads from Google Fonts on page load. Hanken Grotesk is the Brand Pack v3 body font (friendly, accessible).',
  },
  {
    key: 'brand_logo_key',
    label: 'Custom logo',
    description: 'R2 storage key for the uploaded logo. Empty string uses the built-in WWAAVE ripple mark + wordmark.',
    category: 'branding',
    valueType: 'string',
    default: '',
    policyNote:
      'Upload via the Branding tab. Recommended: SVG or transparent PNG, max 500 KB. Leave empty to use the built-in brand mark (recolours for dark mode automatically).',
  },
] as const satisfies readonly PlatformSettingDef[];

export type PlatformSettingKey = (typeof PLATFORM_SETTINGS)[number]['key'];

const PLATFORM_SETTING_BY_KEY: Record<string, PlatformSettingDef> = Object.freeze(
  Object.fromEntries(PLATFORM_SETTINGS.map((s) => [s.key, s])),
);

export function getPlatformSettingDef(key: string): PlatformSettingDef | undefined {
  return PLATFORM_SETTING_BY_KEY[key];
}

export function isPlatformSettingKey(value: unknown): value is PlatformSettingKey {
  return typeof value === 'string' && PLATFORM_SETTING_BY_KEY[value] !== undefined;
}

export function allPlatformSettingKeys(): readonly PlatformSettingKey[] {
  return PLATFORM_SETTINGS.map((s) => s.key) as readonly PlatformSettingKey[];
}

/**
 * Validate + coerce a raw value against a setting's registry definition.
 * Returns the parsed value on success; throws on failure with a precise
 * message the route handler can return to the client.
 */
export function validatePlatformSettingValue(
  def: PlatformSettingDef,
  raw: unknown,
): number | string | boolean {
  if (def.valueType === 'number') {
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
    if (!Number.isFinite(n)) {
      throw new Error(`Setting '${def.key}' expects a number; received ${typeof raw}.`);
    }
    if (def.min !== undefined && n < def.min) {
      throw new Error(`Setting '${def.key}' must be >= ${def.min}.`);
    }
    if (def.max !== undefined && n > def.max) {
      throw new Error(`Setting '${def.key}' must be <= ${def.max}.`);
    }
    return n;
  }
  if (def.valueType === 'string') {
    if (typeof raw !== 'string') throw new Error(`Setting '${def.key}' expects a string.`);
    if (def.allowedValues && !def.allowedValues.includes(raw)) {
      throw new Error(
        `Setting '${def.key}' must be one of: ${def.allowedValues.join(', ')}.`,
      );
    }
    if (def.pattern && !new RegExp(def.pattern).test(raw)) {
      throw new Error(`Setting '${def.key}' must match pattern: ${def.pattern}.`);
    }
    return raw;
  }
  if (def.valueType === 'boolean') {
    if (typeof raw !== 'boolean') throw new Error(`Setting '${def.key}' expects a boolean.`);
    return raw;
  }
  // valueType === 'json' — no v1 entries use this; we round-trip whatever was passed.
  throw new Error(`Setting '${def.key}' has valueType='json' which is not supported by v1.`);
}
